/**
 * Utility functions for parsing and working with centuries from publication years
 */

/**
 * Parses a publication year string into century information
 * @param {string|null} publicationYear - Publication year in various formats
 * @returns {{century: number|number[]|null, confidence: 'high'|'medium'|'low', raw: string}}
 */
export const parseCentury = (publicationYear) => {
    if (!publicationYear || typeof publicationYear !== 'string') {
        return { century: null, confidence: 'low', raw: publicationYear || '' };
    }

    const trimmed = publicationYear.trim();

    // Pattern 1: Exact year (e.g., "1949")
    const exactYearMatch = trimmed.match(/^(\d{4})$/);
    if (exactYearMatch) {
        const year = parseInt(exactYearMatch[1], 10);
        const century = Math.ceil(year / 100);
        return { century, confidence: 'high', raw: trimmed };
    }

    // Pattern 2: Czech century notation (e.g., "19. století")
    const centuryMatch = trimmed.match(/^(\d+)\.\s*stolet[ií]/i);
    if (centuryMatch) {
        const century = parseInt(centuryMatch[1], 10);
        return { century, confidence: 'medium', raw: trimmed };
    }

    // Pattern 3: Year range (e.g., "1850-1860")
    const yearRangeMatch = trimmed.match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (yearRangeMatch) {
        const startYear = parseInt(yearRangeMatch[1], 10);
        const endYear = parseInt(yearRangeMatch[2], 10);
        const centuries = getCenturyRange(startYear, endYear);
        return {
            century: centuries.length === 1 ? centuries[0] : centuries,
            confidence: 'medium',
            raw: trimmed
        };
    }

    // Pattern 4: Century range (e.g., "19-20. století")
    const centuryRangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)\.\s*stolet[ií]/i);
    if (centuryRangeMatch) {
        const startCentury = parseInt(centuryRangeMatch[1], 10);
        const endCentury = parseInt(centuryRangeMatch[2], 10);
        const centuries = [];
        for (let c = startCentury; c <= endCentury; c++) {
            centuries.push(c);
        }
        return {
            century: centuries.length === 1 ? centuries[0] : centuries,
            confidence: 'medium',
            raw: trimmed
        };
    }

    // Pattern 5: Approximate year with "cca", "kolem", "asi" (e.g., "cca 1850")
    const approxYearMatch = trimmed.match(/(cca|kolem|asi)\s*(\d{4})/i);
    if (approxYearMatch) {
        const year = parseInt(approxYearMatch[2], 10);
        const century = Math.ceil(year / 100);
        return { century, confidence: 'medium', raw: trimmed };
    }

    // Pattern 6: Year with suffix (e.g., "1949/1950")
    const yearWithSuffixMatch = trimmed.match(/^(\d{4})\/(\d{4})$/);
    if (yearWithSuffixMatch) {
        const year1 = parseInt(yearWithSuffixMatch[1], 10);
        const year2 = parseInt(yearWithSuffixMatch[2], 10);
        const centuries = getCenturyRange(year1, year2);
        return {
            century: centuries.length === 1 ? centuries[0] : centuries,
            confidence: 'medium',
            raw: trimmed
        };
    }

    // If no pattern matched, return null with low confidence
    return { century: null, confidence: 'low', raw: trimmed };
};

/**
 * Gets all centuries covered by a year range
 * @param {number} startYear - Start year
 * @param {number} endYear - End year
 * @returns {number[]} Array of centuries
 */
export const getCenturyRange = (startYear, endYear) => {
    const startCentury = Math.ceil(startYear / 100);
    const endCentury = Math.ceil(endYear / 100);

    const centuries = [];
    for (let c = startCentury; c <= endCentury; c++) {
        centuries.push(c);
    }

    return centuries;
};

/**
 * Formats a century number to Czech format
 * @param {number} century - Century number (e.g., 19, 20)
 * @returns {string} Formatted century (e.g., "19. století")
 */
export const formatCentury = (century) => {
    if (typeof century !== 'number' || !Number.isInteger(century)) {
        return '';
    }
    return `${century}. století`;
};

/**
 * Checks if a book's publication year matches a specific century
 * @param {string|null} publicationYear - Book's publication year
 * @param {number} targetCentury - Century to check against
 * @returns {boolean} True if the book is from the target century
 */
export const isFromCentury = (publicationYear, targetCentury) => {
    const parsed = parseCentury(publicationYear);

    if (!parsed.century) {
        return false;
    }

    // Handle single century
    if (typeof parsed.century === 'number') {
        return parsed.century === targetCentury;
    }

    // Handle multiple centuries (range)
    if (Array.isArray(parsed.century)) {
        return parsed.century.includes(targetCentury);
    }

    return false;
};

/**
 * Extracts a numeric year from publication year string
 * @param {string|null} publicationYear - Book's publication year
 * @returns {number|null} Numeric year or null if cannot be determined
 */
export const extractYear = (publicationYear) => {
    if (!publicationYear || typeof publicationYear !== 'string') {
        return null;
    }

    const trimmed = publicationYear.trim();

    // Pattern 1: Exact year (e.g., "1949")
    const exactYearMatch = trimmed.match(/^(\d{4})$/);
    if (exactYearMatch) {
        return parseInt(exactYearMatch[1], 10);
    }

    // Pattern 2: Year range - take the first year (e.g., "1850-1860")
    const yearRangeMatch = trimmed.match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (yearRangeMatch) {
        return parseInt(yearRangeMatch[1], 10);
    }

    // Pattern 3: Czech century notation - convert to approximate year (e.g., "19. století" -> 1900)
    const centuryMatch = trimmed.match(/^(\d+)\.\s*stolet[ií]/i);
    if (centuryMatch) {
        const century = parseInt(centuryMatch[1], 10);
        return (century - 1) * 100 + 1; // Start of century
    }

    // Pattern 4: Approximate year with "cca", "kolem", "asi" (e.g., "cca 1850")
    const approxYearMatch = trimmed.match(/(cca|kolem|asi)\s*(\d{4})/i);
    if (approxYearMatch) {
        return parseInt(approxYearMatch[2], 10);
    }

    return null;
};

/**
 * Checks if a book's publication year falls within a year range
 * @param {string|null} publicationYear - Book's publication year
 * @param {number|null} yearFrom - Start year (inclusive), null means no lower bound
 * @param {number|null} yearTo - End year (inclusive), null means no upper bound
 * @returns {boolean} True if the book is within the year range
 */
export const isInYearRange = (publicationYear, yearFrom, yearTo) => {
    const year = extractYear(publicationYear);

    if (year === null) {
        // Unknown publication date: assume very old — satisfies rules with only an upper bound
        // (e.g. "do konce 18. stol."), but not rules with a lower bound.
        return yearFrom === null && yearTo !== null;
    }

    // Check lower bound
    if (yearFrom !== null && year < yearFrom) {
        return false;
    }

    // Check upper bound
    if (yearTo !== null && year > yearTo) {
        return false;
    }

    return true;
};
